import { take } from 'lodash';
import { Moment } from 'moment';
import { Count } from 'api/models';
import {
  Start,
  NotificationGroup,
  NotificationStatus,
} from 'api/notifications';

export type NotificationCount = ['notification_count', number];
export const NotificationCount = (count: number): NotificationCount => [
  'notification_count',
  count,
];

export type FetchInfo = ['fetch_info', Count, Start, NotificationCount];
export const FetchInfo = (
  count: Count,
  start: Start,
  notifications: NotificationCount,
): FetchInfo => ['fetch_info', count, start, notifications];

export type Loading<T> = ['loading', T];
export const Loading = <T>(item: T): Loading<T> => ['loading', item];

export type Loaded<T> = ['loaded', T];
export const Loaded = <T>(item: T): Loaded<T> => ['loaded', item];

export type Deleted<T> = ['deleted', T];
export const Deleted = <T>(item: T): Deleted<T> => ['deleted', item];

type Clearable = { status: NotificationStatus.New; clear: () => void };
type Unclearable = { status: NotificationStatus.Cleared; unclear: () => void };
type Queryable = { loadMore: () => void };

export type ActionableGroup = NotificationGroup &
  (Clearable | Unclearable) &
  Partial<Queryable>;
export type Group =
  | Loading<ActionableGroup>
  | Loaded<ActionableGroup>
  | Deleted<ActionableGroup>;

export type Empty = ['empty'];
export const Empty: Empty = ['empty'];

type LastSeenAt = Moment;

export type FetchingFirstPage = ['fetching_first_page', Count, LastSeenAt];
export const FetchingFirstPage = (
  count: Count,
  moment: LastSeenAt,
): FetchingFirstPage => ['fetching_first_page', count, moment];

export type LoadMore = () => void;

export type Fetched = ['fetched', FetchInfo, Group[], LastSeenAt, LoadMore];
export const Fetched = (
  info: FetchInfo,
  previous: Group[],
  current: Group[],
  moment: LastSeenAt,
  loadMore: (info: FetchInfo, next: Start) => LoadMore,
): Fetched => {
  const count = info[1][1];
  const actual = previous.concat(take(current, count));
  const next = current[count]![1].notifications[0].sortKey;

  return ['fetched', info, actual, moment, loadMore(info, Start(next))];
};

export type Fetching = ['fetching', FetchInfo, Group[], LastSeenAt];
export const Fetching = (
  info: FetchInfo,
  groups: Group[],
  moment: LastSeenAt,
): Fetching => ['fetching', info, groups, moment];

export type FetchedLastPage = [
  'fetched_last_page',
  FetchInfo,
  Group[],
  LastSeenAt,
];
export const FetchedLastPage = (
  info: FetchInfo,
  groups: Group[],
  moment: LastSeenAt,
): FetchedLastPage => ['fetched_last_page', info, groups, moment];

export const setGroups = <T extends Fetched | FetchedLastPage>(
  [kind, info, , requestedAt, loadMore]: T,
  groups: Group[],
) => [kind, info, groups, requestedAt, loadMore] as T;

export type Content =
  | Empty
  | FetchingFirstPage
  | Fetched
  | Fetching
  | FetchedLastPage;

export type PendingUpdatesCount = ['pending_updates_count', number];
export const PendingUpdatesCount = (count: number): PendingUpdatesCount => [
  'pending_updates_count',
  count,
];

export type Refresh = () => void;
